import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CompactBand, BandTab } from '@/components/CompactBand';
import { FOLLOW_UP_SETTINGS_PATH, useFlagSettings } from '@/features/insurance-ar/flagSettings';
import type { Tab } from '../types';
import { money } from '../utils';

export type View = 'denied' | 'underpaid' | 'appeals' | 'closed';

interface HeaderProps {
  view: View;
  deniedCount: number;
  deniedTotal: number;
  underpaidCount: number;
  underpaidShort: number;
  appealsCount: number;
  /** Rows on the merged "Closed" pill (resolved history + appeals closed). */
  closedCount: number;
  /** The "Needs you" group across both tabs — sets the amber em. */
  needsYouCount: number;
  tabTo: (view: View) => string;
}

/** The amber em: 0 → none, 1 → "One needs you.", 2+ → "Two need you." */
function needsYouEm(n: number): ReactNode {
  if (n <= 0) return null;
  return <em>{n === 1 ? 'One needs you.' : 'Two need you.'}</em>;
}

function headlineFor(
  deniedCount: number,
  deniedTotal: number,
  underpaidCount: number,
  underpaidShort: number,
  needsYouCount: number,
): ReactNode {
  const em = needsYouEm(needsYouCount);
  const tail = em ? <> {em}</> : null;
  if (deniedCount === 0 && underpaidCount === 0) {
    return <>Nothing came back denied or short.</>;
  }
  if (underpaidCount === 0) {
    return (
      <>
        {deniedCount} {deniedCount === 1 ? 'claim was' : 'claims were'} denied, {money(deniedTotal)}.{tail}
      </>
    );
  }
  if (deniedCount === 0) {
    return (
      <>
        {underpaidCount} {underpaidCount === 1 ? 'claim was' : 'claims were'} paid short, {money(underpaidShort)} in all.{tail}
      </>
    );
  }
  return (
    <>
      {deniedCount} denied and {underpaidCount} underpaid, {money(deniedTotal + underpaidShort)} in all.{tail}
    </>
  );
}

export function Header({
  view,
  deniedCount,
  deniedTotal,
  underpaidCount,
  underpaidShort,
  appealsCount,
  closedCount,
  needsYouCount,
  tabTo,
}: HeaderProps) {
  const { toleranceDollars } = useFlagSettings();
  return (
    <CompactBand
      headline={headlineFor(deniedCount, deniedTotal, underpaidCount, underpaidShort, needsYouCount)}
      right={<span>Differences under <Link to={FOLLOW_UP_SETTINGS_PATH}>${toleranceDollars}</Link> aren’t chased</span>}
      tabs={
        <>
          <BandTab
            label="Denied"
            meta={`${String(deniedCount)} · ${money(deniedTotal)}`}
            active={view === 'denied'}
            to={tabTo('denied')}
          />
          <BandTab
            label="Underpaid"
            meta={`${String(underpaidCount)} · ${money(underpaidShort)} short`}
            active={view === 'underpaid'}
            to={tabTo('underpaid')}
          />
          {/* "Sent" — the tab text shortens to fit four pills; the route stays /denied/appeals. */}
          <BandTab
            label="Sent"
            meta={appealsCount}
            active={view === 'appeals'}
            to={tabTo('appeals')}
          />
          <BandTab
            label="Closed"
            meta={closedCount}
            active={view === 'closed'}
            to={tabTo('closed')}
          />
        </>
      }
    />
  );
}

export function tabForKind(kind: Tab): View {
  return kind;
}
